import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import { activarPush, desactivarPush, estadoDelPush, type EstadoDelPush } from './push';
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { api, getAccessToken, type ApiEnvelope } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { escucharNotificaciones, type NotificacionEmpujada } from './canal';

/**
 * Ofrece activar los avisos en el móvil — tarea 8.14.
 *
 * NO SE ENSEÑA SI NO SE PUEDE. Un navegador sin push, una instalación sin
 * claves VAPID o un permiso ya denegado no ven ningún botón: ofrecer algo que
 * no va a funcionar es peor que no ofrecerlo, porque el fallo parece del
 * producto.
 */
function AvisosEnElMovil() {
  const [estado, setEstado] = useState<EstadoDelPush | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    void estadoDelPush().then(setEstado);
  }, []);

  if (estado === null || estado === 'no-soportado' || estado === 'no-configurado') return null;

  if (estado === 'denegado') {
    return (
      <p className="text-fg-muted border-border border-b px-3 py-2 text-xs">
        Has bloqueado los avisos en este navegador. Para recibirlos hay que permitirlos desde su
        configuración de notificaciones.
      </p>
    );
  }

  return (
    <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
      <span className="text-fg-muted text-xs">
        {estado === 'activo'
          ? 'Recibes avisos en este dispositivo.'
          : 'Recibe los avisos aunque no tengas la aplicación abierta.'}
      </span>
      <Button
        size="xs"
        variant="ghost"
        disabled={ocupado}
        onClick={() => {
          setOcupado(true);
          const accion = estado === 'activo' ? desactivarPush() : activarPush();
          void accion
            .then(setEstado)
            .catch(() => undefined)
            .finally(() => setOcupado(false));
        }}
      >
        {estado === 'activo' ? 'Desactivar' : 'Activar'}
      </Button>
    </div>
  );
}

/**
 * La campana — tarea 7.11 · plan.md §25.1
 *
 * EL CONTADOR SALE DEL SERVIDOR, no de contar lo que hay en el panel: el panel
 * trae como mucho cincuenta y quien vuelve de vacaciones puede tener más.
 *
 * EL TIEMPO REAL ES UN AÑADIDO, NO EL ÚNICO CAMINO. Cuando llega un empujón por
 * el canal se refresca la lista; pero además se vuelve a preguntar al abrir el
 * panel y cada dos minutos. Si el canal se cae —una obra sin cobertura, un
 * proxy que corta— la campana sigue funcionando, sólo que más despacio. Una
 * campana que depende de una conexión abierta es una campana que un día se
 * queda muda sin decirlo.
 *
 * NOTA SOBRE LA COMPROBACIÓN: el mecanismo completo —canal abierto, evento que
 * llega solo en segundos, aislamiento entre personas— está verificado de punta
 * a punta en `verify:notifications`, que abre el canal con `fetch` igual que
 * esto y espera el evento. NO se ha podido comprobar dentro del panel de vista
 * previa del entorno de desarrollo: ese panel se bloquea entero mientras haya
 * un `fetch` en streaming abierto —deja de responder hasta a la navegación—, lo
 * cual es una limitación suya y no del producto. En un navegador de verdad la
 * conexión cuesta una de las seis de HTTP/1.1, y ninguna con HTTP/2.
 */

interface Notificacion {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  createdAt: string;
}

interface Respuesta {
  data: Notificacion[];
  meta: { unreadCount: number };
}

function haceCuanto(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'ahora';
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'ayer' : `hace ${dias} días`;
}

export function Campana() {
  const [abierto, setAbierto] = useState(false);
  const [enVivo, setEnVivo] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const notificaciones = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<Respuesta>('/notifications'),
    // La red de seguridad de la que habla el comentario de arriba.
    refetchInterval: 120_000,
  });

  const marcar = useMutation({
    mutationFn: (ids?: string[]) =>
      api.post<ApiEnvelope<{ unreadCount: number }>>('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // ── El canal ──────────────────────────────────────────────────────────────
  //
  // Se abre UNA vez, al montar. `onNotificacion` invalida la consulta en lugar
  // de añadir la notificación a mano: así el contador y la lista salen siempre
  // de la misma fuente y no pueden discrepar.
  const invalidar = useRef(() => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  useEffect(() => {
    const cerrar = escucharNotificaciones({
      token: () => getAccessToken(),
      onNotificacion: (_n: NotificacionEmpujada) => invalidar.current(),
      onEstado: setEnVivo,
    });

    return cerrar;
  }, []);

  const sinLeer = notificaciones.data?.meta.unreadCount ?? 0;
  const lista = notificaciones.data?.data ?? [];

  const abrirNotificacion = (n: Notificacion) => {
    if (!n.read) marcar.mutate([n.id]);
    setAbierto(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover
      open={abierto}
      onOpenChange={(v) => {
        setAbierto(v);
        // Al abrir se vuelve a preguntar: es el momento en que más importa que
        // lo que se ve sea lo que hay.
        if (v) void notificaciones.refetch();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={sinLeer > 0 ? `Notificaciones, ${sinLeer} sin leer` : 'Notificaciones'}
        >
          <Bell />
          {sinLeer > 0 && (
            <Badge
              tone="danger"
              className="absolute -top-0.5 -right-0.5 size-4 justify-center p-0 text-[10px]"
            >
              {sinLeer > 9 ? '9+' : sinLeer}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <header className="border-border flex items-center justify-between border-b px-3 py-2">
          <span className="text-fg text-sm font-medium">
            Notificaciones
            {/* El punto verde dice si el canal está vivo. No es decorativo:
                sin él, «no me llega nada» y «no ha pasado nada» se parecen. */}
            <span
              className={cn(
                'ml-2 inline-block size-1.5 rounded-full align-middle',
                enVivo ? 'bg-success' : 'bg-fg-subtle',
              )}
              title={enVivo ? 'Conectado en tiempo real' : 'Sin conexión en vivo; se refresca sola'}
            />
          </span>

          {sinLeer > 0 && (
            <Button size="xs" variant="ghost" onClick={() => marcar.mutate(undefined)}>
              <Check />
              Marcar todo
            </Button>
          )}
        </header>

        {/* EL BOTÓN DE PUSH VIVE AQUÍ Y NO EN UN AJUSTE ESCONDIDO — tarea
            8.14. Quien abre la campana está pensando en avisos justo en ese
            momento: es el único sitio donde ofrecerlo tiene sentido, y el
            permiso del navegador sólo se puede pedir una vez. */}
        <AvisosEnElMovil />

        <div className="max-h-96 overflow-y-auto">
          {lista.length === 0 ? (
            <p className="text-fg-muted px-3 py-8 text-center text-sm">No tienes notificaciones.</p>
          ) : (
            <ul className="divide-border divide-y">
              {lista.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => abrirNotificacion(n)}
                    className={cn(
                      'hover:bg-surface-2 w-full px-3 py-2 text-left',
                      !n.read && 'bg-brand-soft/20',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span className="bg-brand mt-1.5 size-1.5 shrink-0 rounded-full" />
                      )}
                      <div className={cn('min-w-0 flex-1', n.read && 'pl-3.5')}>
                        <p className="text-fg text-sm font-medium">{n.title}</p>
                        {n.body && <p className="text-fg-muted truncate text-xs">{n.body}</p>}
                        <p className="text-fg-subtle mt-0.5 text-xs">{haceCuanto(n.createdAt)}</p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
